'use client';

import React, { useState, useCallback, memo, useMemo, FC } from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronRight, ImageIcon, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { industryCardVariants, ctaButtonVariants, Heading, Text } from '@/components/ui/design-system';
import type { IndustryApplication } from '@/lib/data/industries';

export interface ApplicationCardProps {
  application: IndustryApplication;
  index: number;
  className?: string;
  isPriority?: boolean;
}

const DEFAULT_APP_IMAGE = "/images/pages/industries/applications/default.jpg";

const ApplicationCard: FC<ApplicationCardProps> = memo(({
  application,
  index = 0,
  className = '',
  isPriority = false
}) => {
  const prefersReducedMotion = useReducedMotion();
  const [imageError, setImageError] = useState(false);

  const imageSource = useMemo(
    () => (imageError || !application.image_url ? DEFAULT_APP_IMAGE : application.image_url),
    [imageError, application.image_url]
  );

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  return (
    <motion.article
      key={application.id}
      variants={prefersReducedMotion ? {} : industryCardVariants}
      initial={prefersReducedMotion ? {} : "hidden"}
      animate={prefersReducedMotion ? {} : "visible"}
      whileHover={prefersReducedMotion ? {} : "hover"}
      whileFocus={prefersReducedMotion ? {} : "focus"}
      custom={index}
      className={cn(
        "group bg-white dark:bg-gray-800 rounded-2xl overflow-hidden relative flex flex-col transition-all duration-300 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-lg",
        className
      )}
      tabIndex={0}
      role="group"
      aria-labelledby={`app-title-${application.id}`}
      style={{ willChange: 'transform, box-shadow' }}
    >
      <div className="relative w-full h-52 overflow-hidden rounded-t-2xl ring-1 ring-black/5 dark:ring-white/10 ring-inset">
        {imageError || !application.image_url ? (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
            <div className="flex flex-col items-center text-gray-400 dark:text-gray-500">
              <ImageIcon className="w-10 h-10 mb-2" aria-hidden="true" />
              <span className="text-xs text-center">{application.name}</span>
            </div>
          </div>
        ) : (
          <Image
            src={imageSource}
            alt={`${application.name} - Application illustration`}
            fill
            className="object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
            sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
            onError={handleImageError}
            priority={isPriority}
            loading={isPriority ? "eager" : "lazy"}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" aria-hidden="true"></div>
        {/* Title moved to content for consistency with IndustryCard */}
      </div>
      
      <div className="p-6 flex-1 flex flex-col">
        <Heading level={3} className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
          {application.name}
        </Heading>
        <Text variant="body" className="mb-4 flex-1">
          {application.description}
        </Text>

        {application.features && application.features.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Key Features:</h4>
            <ul className="space-y-2">
              {application.features.map((feature, i) => (
                <li key={i} className="flex items-start text-sm">
                  <CheckCircle2 className="w-4 h-4 text-primary-500 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600 dark:text-gray-300">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        <motion.div 
          className="mt-auto pt-4 flex items-center text-primary-600 dark:text-primary-400 font-medium text-sm cursor-not-allowed"
          variants={prefersReducedMotion ? {} : ctaButtonVariants}
          initial={prefersReducedMotion ? {} : "initial"}
          onClick={(e) => e.preventDefault()}
        >
          <span>Explore solutions</span>
          <ChevronRight className="w-4 h-4 ml-1" />
        </motion.div>
      </div>
    </motion.article>
  );
});

ApplicationCard.displayName = 'ApplicationCard';

export default ApplicationCard;
